import React, { useCallback, useEffect, useState } from 'react';
import { AlertOctagon, Plus, Trash2, Save, Eye, EyeOff, RefreshCw, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_OPTIONS = [
  { value: 'any',  label: 'Anywhere', hint: 'Match in name OR body' },
  { value: 'name', label: 'Name only',  hint: 'Match author name field' },
  { value: 'body', label: 'Body only',  hint: 'Match comment text' },
];

const fmt = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  } catch { return iso; }
};

const BanTriggersTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [newPattern, setNewPattern] = useState('');
  const [newKind, setNewKind] = useState('any');
  const [newReason, setNewReason] = useState('');

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/admin/ban-triggers`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load triggers');
      const data = await res.json();
      setRows(data.triggers || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newPattern.trim()) return;
    setBusy('new');
    try {
      const res = await fetch(`${API}/api/admin/ban-triggers`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern: newPattern.trim(),
          kind: newKind,
          reason: newReason.trim() || 'Auto-ban trigger word',
          active: true,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || 'Create failed');
      }
      setNewPattern(''); setNewReason(''); setNewKind('any');
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const patch = async (id, updates) => {
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/ban-triggers/${id}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || 'Update failed');
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this trigger? Already-banned IPs stay banned.')) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/ban-triggers/${id}`, {
        method: 'DELETE', headers: auth,
      });
      if (!res.ok) throw new Error('Delete failed');
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const updateField = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const activeCount = rows.filter((r) => r.active).length;
  const totalHits = rows.reduce((sum, r) => sum + (r.hits || 0), 0);

  return (
    <div className="space-y-5" data-testid="ban-triggers-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AlertOctagon className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-black tracking-widest text-red-400 uppercase">
              Ban Triggers
            </h3>
          </div>
          <p className="text-sm text-gray-400">
            Any comment matching one of these patterns is <strong className="text-white">rejected</strong> and
            the poster&apos;s IP is <strong className="text-white">permanently blocked</strong> from the
            entire site. Case-insensitive whole-word match - <code className="text-red-300 bg-black/40 px-1 rounded">ryan naumenko</code>{' '}
            catches &quot;Ryan Naumenko&quot; and &quot;RYAN NAUMENKO&quot; but not &quot;ryan naumenkoo&quot;.
            <span className="ml-2 text-emerald-400 font-bold">{activeCount} active</span> ·{' '}
            <span className="text-red-300 font-bold">{totalHits} total hits</span>
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ban-triggers-refresh"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Create form */}
      <form
        onSubmit={create}
        className="bg-zinc-900/80 backdrop-blur-xl border border-red-500/25 rounded-lg p-4 space-y-3"
        data-testid="ban-trigger-create-form"
      >
        <p className="text-xs font-black tracking-widest text-red-400 uppercase flex items-center gap-1.5">
          <Target className="h-3.5 w-3.5" /> Add new trigger
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            required
            value={newPattern}
            onChange={(e) => setNewPattern(e.target.value)}
            placeholder="Trigger word or phrase"
            data-testid="ban-trigger-new-pattern"
            maxLength={120}
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value)}
            data-testid="ban-trigger-new-kind"
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Reason (shown in blocklist)"
            data-testid="ban-trigger-new-reason"
            maxLength={200}
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
        </div>
        <button
          type="submit"
          disabled={busy === 'new' || !newPattern.trim()}
          data-testid="ban-trigger-add-btn"
          className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 font-black text-xs uppercase tracking-widest px-5 py-2 rounded flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Trigger
        </button>
        <p className="text-[10px] uppercase tracking-widest text-gray-500">
          Tip: use &quot;Name only&quot; for impersonation traps · &quot;Body only&quot; for keyword filters ·
          &quot;Anywhere&quot; for slurs and doxxing terms.
        </p>
      </form>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm" data-testid="ban-trigger-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="ban-trigger-loading">Loading triggers…</div>
      ) : rows.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="ban-trigger-empty">
          No triggers yet. Add one above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ban-trigger-list">
          {rows.map((r) => {
            const isBusy = busy === r.id;
            return (
              <div
                key={r.id}
                data-testid={`ban-trigger-item-${r.id}`}
                className={`bg-zinc-900/80 backdrop-blur-xl border rounded-lg p-3 space-y-3 ${
                  r.active ? 'border-red-500/25' : 'border-zinc-700/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">
                          Pattern
                        </label>
                        <input
                          value={r.pattern}
                          onChange={(e) => updateField(r.id, 'pattern', e.target.value)}
                          data-testid={`ban-trigger-pattern-${r.id}`}
                          maxLength={120}
                          className="w-full bg-black/50 border border-red-500/25 text-red-200 font-mono px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">
                          Match In
                        </label>
                        <select
                          value={r.kind}
                          onChange={(e) => updateField(r.id, 'kind', e.target.value)}
                          data-testid={`ban-trigger-kind-${r.id}`}
                          className="w-full bg-black/50 border border-red-500/25 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                        >
                          {KIND_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">
                          Reason (public)
                        </label>
                        <input
                          value={r.reason || ''}
                          onChange={(e) => updateField(r.id, 'reason', e.target.value)}
                          data-testid={`ban-trigger-reason-${r.id}`}
                          maxLength={200}
                          className="w-full bg-black/50 border border-red-500/25 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500">
                      <span>Created {fmt(r.created_at)}</span>
                      <span>·</span>
                      <span className="text-red-300 font-bold">{r.hits || 0} hits</span>
                      {r.last_hit_at && (
                        <>
                          <span>·</span>
                          <span>Last hit {fmt(r.last_hit_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 flex-wrap">
                  <button
                    onClick={() => patch(r.id, { active: !r.active })}
                    disabled={isBusy}
                    data-testid={`ban-trigger-toggle-${r.id}`}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-bold ${
                      r.active
                        ? 'bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-200'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-gray-400'
                    }`}
                  >
                    {r.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {r.active ? 'ACTIVE' : 'DISABLED'}
                  </button>
                  <button
                    onClick={() => patch(r.id, {
                      pattern: r.pattern, kind: r.kind, reason: r.reason || '',
                    })}
                    disabled={isBusy}
                    data-testid={`ban-trigger-save-${r.id}`}
                    className="flex items-center gap-1 bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 font-black text-xs px-3 py-1.5 rounded"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    disabled={isBusy}
                    data-testid={`ban-trigger-delete-${r.id}`}
                    className="flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 text-red-200 font-bold text-xs px-3 py-1.5 rounded"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BanTriggersTab;
